import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Download,
  Print,
  AttachMoney,
  Category,
  CalendarToday,
  Person,
  TrendingUp,
  TrendingDown,
  Assessment,
  History,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

interface CostCodeTransaction {
  _id: string;
  date: string;
  description: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  project: string;
  enteredBy: {
    firstName: string;
    lastName: string;
  };
}

interface CostCodeDetail {
  _id: string;
  code: string;
  description: string;
  category: 'labor' | 'material' | 'equipment' | 'subcontractor' | 'overhead';
  unit: string;
  unitCost: number;
  budgetAmount: number;
  actualAmount: number;
  variance: number;
  variancePercentage: number;
  status: 'active' | 'inactive' | 'archived';
  project?: {
    name: string;
    _id: string;
  };
  createdDate: string;
  lastUpdated: string;
  createdBy: {
    firstName: string;
    lastName: string;
  };
  updatedBy: {
    firstName: string;
    lastName: string;
  };
  transactions: CostCodeTransaction[];
  notes: string;
}

const CostCodeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [costCode, setCostCode] = useState<CostCodeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const mapCostCode = (cc: any): CostCodeDetail => ({
      _id: cc._id || cc.id || id || '',
      code: cc.code || cc.name || '',
      description: cc.description || cc.name || '',
      category: (cc.category || 'labor') as CostCodeDetail['category'],
      unit: cc.unit || cc.unitOfMeasure || 'each',
      unitCost: Number(cc.unitCost ?? cc.standardRate ?? 0),
      budgetAmount: Number(cc.budgetAmount ?? 0),
      actualAmount: Number(cc.actualAmount ?? 0),
      variance: Number(cc.variance ?? (Number(cc.actualAmount ?? 0) - Number(cc.budgetAmount ?? 0))),
      variancePercentage: Number(cc.variancePercentage ?? 0),
      status: (cc.status || 'active') as CostCodeDetail['status'],
      project: cc.project ? { name: cc.project?.name || 'Unassigned', _id: cc.project?._id || '' } : undefined,
      createdDate: cc.createdAt || cc.createdDate || new Date().toISOString(),
      lastUpdated: cc.updatedAt || cc.lastUpdated || new Date().toISOString(),
      createdBy: {
        firstName: cc.createdBy?.firstName || 'System',
        lastName: cc.createdBy?.lastName || '',
      },
      updatedBy: {
        firstName: cc.updatedBy?.firstName || 'System',
        lastName: cc.updatedBy?.lastName || '',
      },
      transactions: Array.isArray(cc.transactions) ? cc.transactions.map((t: any) => ({
        _id: t._id || t.id || '',
        date: t.date || t.createdAt || new Date().toISOString(),
        description: t.description || '',
        quantity: Number(t.quantity ?? 0),
        unitCost: Number(t.unitCost ?? 0),
        totalCost: Number(t.totalCost ?? Number(t.quantity ?? 0) * Number(t.unitCost ?? 0)),
        project: t.project?.name || t.projectName || '',
        enteredBy: {
          firstName: t.enteredBy?.firstName || 'System',
          lastName: t.enteredBy?.lastName || '',
        },
      })) : [],
      notes: cc.notes || '',
    });

    const fetchCostCode = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id) throw new Error('Invalid cost code ID');
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/cost-codes/${id}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        if (!res.ok) throw new Error('Failed to fetch cost code');
        const data = await res.json();
        const item = data?.data ?? data;
        setCostCode(mapCostCode(item));
      } catch (e: any) {
        console.error('Error fetching cost code detail:', e);
        setError(e?.message || 'Error fetching cost code');
        setCostCode(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCostCode();
  }, [id]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'labor':
        return 'primary';
      case 'material':
        return 'secondary';
      case 'equipment':
        return 'warning';
      case 'subcontractor':
        return 'info';
      case 'overhead':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return 'error';
    if (variance < 0) return 'success';
    return 'default';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading cost code details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!costCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cost code not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/cost-codes')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            {costCode.code}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {costCode.description}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
          <Button variant="outlined" startIcon={<Download />}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Cost Code Information */}
        <Box sx={{ flex: { xs: 1, md: 2 } }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Code Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Category
                    </Typography>
                    <Chip
                      label={costCode.category.charAt(0).toUpperCase() + costCode.category.slice(1)}
                      color={getCategoryColor(costCode.category) as any}
                      icon={<Category />}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Unit
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {costCode.unit}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Unit Cost
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(costCode.unitCost)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={costCode.status.charAt(0).toUpperCase() + costCode.status.slice(1)}
                      color={getStatusColor(costCode.status) as any}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Project
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {costCode.project?.name || 'General'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(costCode.lastUpdated)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Budget vs Actual */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Budget Analysis
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="white">
                      Budget Amount
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      {formatCurrency(costCode.budgetAmount)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="white">
                      Actual Amount
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      {formatCurrency(costCode.actualAmount)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: costCode.variance < 0 ? 'success.light' : 'error.light', 
                    borderRadius: 1 
                  }}>
                    <Typography variant="body2" color="white">
                      Variance
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      {formatCurrency(costCode.variance)}
                    </Typography>
                    <Typography variant="body2" color="white">
                      ({costCode.variancePercentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Transactions
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit Cost</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Entered By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {costCode.transactions.map((transaction) => (
                      <TableRow key={transaction._id}>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(transaction.date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.quantity} {costCode.unit}s
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(transaction.unitCost)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(transaction.totalCost)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.enteredBy.firstName} {transaction.enteredBy.lastName}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: { xs: 1, md: 1 } }}>
          {/* Quick Stats */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Stats
              </Typography>
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Assessment color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Transactions"
                    secondary={costCode.transactions.length}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <AttachMoney color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Average Transaction"
                    secondary={formatCurrency(
                      costCode.transactions.reduce((sum, t) => sum + t.totalCost, 0) / costCode.transactions.length
                    )}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    {costCode.variance < 0 ? (
                      <TrendingDown color="success" />
                    ) : (
                      <TrendingUp color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Budget Performance"
                    secondary={costCode.variance < 0 ? 'Under Budget' : 'Over Budget'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Audit Trail */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Audit Trail
              </Typography>
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CalendarToday fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Created"
                    secondary={`${formatDate(costCode.createdDate)} by ${costCode.createdBy.firstName} ${costCode.createdBy.lastName}`}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <History fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Last Modified"
                    secondary={`${formatDate(costCode.lastUpdated)} by ${costCode.updatedBy.firstName} ${costCode.updatedBy.lastName}`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {costCode.notes}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CostCodeDetail;